/**
 * Badge chatter notes sometimes use API wording "Issued" while proficiency UI uses "Given".
 * Leaves "Issued by …" / "Issued on …" (actor/date) unchanged.
 * @param {unknown} note
 * @returns {string}
 */
export function formatBadgeChatterNote(note) {
  if (note === null || note === undefined) return "";
  const s = String(note);
  if (/^issued$/i.test(s.trim())) return "Given";
  return s.replace(/\bIssued\b(?!\s+(?:by|on)\b)/gi, "Given");
}

/**
 * Organization → Employees drilldown: Path to Readiness & Badges tabs only.
 * Maps API "Given" to "Issued" in chatter. Preserves "Given by …" / "Given on …".
 * @param {unknown} note
 * @returns {string}
 */
export function formatOrganizationEmployeeChatterNote(note) {
  if (note === null || note === undefined) return "";
  const s = String(note);
  if (/^given$/i.test(s.trim())) return "Issued";
  return s.replace(/\bGiven\b(?!\s+(?:by|on)\b)/gi, "Issued");
}

/**
 * Summary renderers for upload-task chatter action codes (Chatter spec Rule 7).
 * These system-anonymous entries are written with an empty/raw note; the human
 * summary is derived from the snapshotted payload at read time. A renderer must
 * never re-read the task/checkpoint — everything it shows comes from the payload.
 */
const CHATTER_RENDERERS = {
  "checkpoint.upload_task_overdue": ({ assignee_name } = {}) =>
    assignee_name ? `Overdue notice sent to ${assignee_name}` : "Overdue notice sent",
  "checkpoint.upload_task_overdue_followup": ({ assignee_name } = {}) =>
    assignee_name ? `Follow-up notice sent to ${assignee_name}` : "Follow-up notice sent",
};

/**
 * Resolve a chatter summary via a registered renderer.
 * @param {string|null|undefined} action_code
 * @param {object|null|undefined} payload
 * @returns {string|null} rendered summary, or null when no renderer exists
 *   (the stored note stays authoritative).
 */
export function renderChatterSummary(action_code, payload) {
  const renderer = CHATTER_RENDERERS[action_code];
  if (!renderer) return null;
  return renderer(payload || {});
}

/**
 * True for a system-anonymous chatter entry (Chatter spec Rule 3):
 * is_system with a null author. System-attributed entries (an author plus
 * is_system) still render under the author's own name.
 * @param {object} el raw chatter row from the API
 * @returns {boolean}
 */
export function isSystemChatterEntry(el = {}) {
  return Boolean(el.is_system) && (el.recorded_by == null || el.recorded_by === "");
}

/**
 * Transforms raw chatter API response data into the format expected by the UI
 * @param {Array} results - Array of chatter items from API response
 * @returns {Array} Transformed chatter items
 */
export const transformChatterData = (results) => {
  return results.map((el) => {
    const date = new Date(el.recorded_on);
    const milliseconds = date.getTime();
    const millisecondsString = milliseconds.toString();
    const newEl = {};
    newEl.id = el.id;
    if (isSystemChatterEntry(el)) {
      newEl.avatar = { picture: "", firstName: "System", lastName: "" };
    } else {
      let nameParts = el.recorded_by_name.split(" ");
      if (nameParts.length === 2) {
        newEl.avatar = {
          picture: "",
          firstName: nameParts[0],
          lastName: nameParts[1],
        };
      } else {
        newEl.avatar = {
          picture: "",
          firstName: el.recorded_by_name,
          lastName: "",
        };
      }
    }
    newEl.IsExIQtiveBot = false;
    const renderedSummary = renderChatterSummary(el.action_code, el.payload);
    newEl.message = renderedSummary != null ? renderedSummary : el.note;
    newEl.created_on = millisecondsString;
    newEl.IsHistory = el.is_history;
    newEl.payload = el.payload || null;
    newEl.action_code = el.action_code || "";
    return newEl;
  });
};
